import React, { useState, useEffect, useMemo } from 'react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import { CheckSquare, Square, Trash2, Loader, GripVertical } from 'lucide-react';
import Button from '../components/ui/Button';
import { useAppStore } from '../store';
import ErrorModal from '../components/modals/ErrorModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import { DndProvider, useDrag, useDrop, XYCoord } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend'; 

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const TodoList: React.FC = () => {
  const { 
    todoItems, 
    fetchTodoItems, 
    addTodoItem, 
    deleteTodoItem, 
    toggleTodoItemCompletion,
    isLoading: globalLoading
  } = useAppStore();

  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [newTaskText, setNewTaskText] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [deleteCompletedConfirmOpen, setDeleteCompletedConfirmOpen] = useState(false);
  const [tasks, setTasks] = useState<TodoItem[]>([]);

  // Store for task order
  const [taskOrder, setTaskOrder] = useState<string[]>([]);

  // Define the drag item type
  const ITEM_TYPE = 'TASK';

  // Fetch todo items on component mount
  useEffect(() => {
    const loadTodoItems = async () => {
      setLocalLoading(true);
      
      try {
        // Add task with current timestamp to ensure it appears at the top
        await fetchTodoItems();
      } catch (error: any) {
        console.error('Error fetching todo items:', error);
        setError(error.message || 'No se pudieron cargar las tareas');
        setShowErrorModal(true);
      } finally {
        setLocalLoading(false);
      }
    };

    loadTodoItems();
  }, [fetchTodoItems]);
  
// Update local tasks state when todoItems changes, preserving user-defined order
useEffect(() => {
  // Load saved order from localStorage
  const savedOrder = localStorage.getItem('todo_items_order');
  const orderArray = savedOrder ? JSON.parse(savedOrder) : [];
  setTaskOrder(orderArray);

  if (orderArray.length > 0) {
    // Create a map for quick lookup
    const orderMap = new Map(orderArray.map((id, index) => [id, index]));
    
    // Sort tasks based on saved order
    const orderedTasks = [...todoItems].sort((a, b) => {
      const orderA = orderMap.has(a.id) ? orderMap.get(a.id)! : Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.has(b.id) ? orderMap.get(b.id)! : Number.MAX_SAFE_INTEGER;
      
      if (orderA === Number.MAX_SAFE_INTEGER && orderB === Number.MAX_SAFE_INTEGER) {
        // For new items not in the order, sort by creation date (newest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      
      return orderA - orderB;
    });
    
    setTasks(orderedTasks);
  } else {
    // If no saved order, sort by creation date (newest first)
    const sortedTasks = [...todoItems].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    setTasks(sortedTasks);
    
    // Initialize order with current IDs
    setTaskOrder(sortedTasks.map(task => task.id));
  }
}, [todoItems]);

// Sort tasks: incomplete first, then completed
// const sortedTodoItems = tasks.sort((a, b) => {
//   // Sort by creation date (newest first)
//   return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
// });

  const handleAddTask = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newTaskText.trim()) {
      try {
        await addTodoItem({
          title: newTaskText.trim(),
          priority: null,
          completed: false
        });
        setNewTaskText('');
      } catch (error: any) {
        console.error('Error adding task:', error);
        setError(error.message || 'Error al agregar la tarea');
        setShowErrorModal(true);
      }
    }
  };

  // Handle task reordering
  const moveTask = (dragIndex: number, hoverIndex: number) => {
    if (dragIndex === hoverIndex) return;
    
    // Create a copy of the tasks array
    const updatedTasks = [...tasks];
    
    // Remove the dragged item
    const draggedItem = updatedTasks[dragIndex];
    
    // Remove the dragged item from the array
    updatedTasks.splice(dragIndex, 1);
    
    // Insert the dragged item at the new position
    updatedTasks.splice(hoverIndex, 0, draggedItem);
    
    // Update the state with the new array
    setTasks(updatedTasks);
    
    // Update and save the order
    const newOrder = updatedTasks.map(task => task.id);
    setTaskOrder(newOrder);
    
    // Save to localStorage
    localStorage.setItem('todo_items_order', JSON.stringify(newOrder));
  };

  // Handle task completion toggle
  const handleToggleComplete = async (id: string, completed: boolean) => {
    try {
      await toggleTodoItemCompletion(id, !completed);
      // Update local state
      setTasks(prev => 
        prev.map(task => task.id === id ? { ...task, completed: !completed } : task)
        // Note: We don't change the order when toggling completion
      );
    } catch (error: any) {
      console.error('Error toggling task completion:', error);
      setError(error.message || 'Error al actualizar el estado de la tarea');
      setShowErrorModal(true);
    }
  };

  // Handle task deletion
  const handleDeleteTask = (id: string) => {
    setTaskToDelete(id);
    setDeleteConfirmOpen(true);
  };

  // Confirm task deletion
  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      await deleteTodoItem(taskToDelete);
      // Update local state
      setTasks(prev => prev.filter(task => task.id !== taskToDelete));
      
      // Update order
      const newOrder = taskOrder.filter(id => id !== taskToDelete);
      setTaskOrder(newOrder);
      // Save to localStorage
      localStorage.setItem('todo_items_order', JSON.stringify(newOrder));
    } catch (error: any) {
      console.error('Error deleting task:', error);
      setError(error.message || 'Error al eliminar la tarea');
      setShowErrorModal(true);
    }
  };

  const completedTasksCount = tasks.filter(task => task.completed).length;

  // Handle delete all completed tasks
  const handleDeleteCompletedTasks = () => {
    if (completedTasksCount > 0) {
      setDeleteCompletedConfirmOpen(true);
    }
  };

  // Confirm delete all completed tasks
  const confirmDeleteCompletedTasks = async () => {
    try {
      const completedTaskIds = tasks
        .filter(task => task.completed)
        .map(task => task.id);
      
      // Delete each completed task
      for (const id of completedTaskIds) {
        await deleteTodoItem(id);
      }
      
      // Update local state
      setTasks(prev => prev.filter(task => !task.completed));
      
      // Update order by removing completed task IDs
      const newOrder = taskOrder.filter(id => !tasks.find(task => task.id === id)?.completed);
      setTaskOrder(newOrder);
      // Save to localStorage
      localStorage.setItem('todo_items_order', JSON.stringify(newOrder));
    } catch (error: any) {
      console.error('Error deleting completed tasks:', error);
      setError(error.message || 'Error al eliminar las tareas completadas');
      setShowErrorModal(true);
    }
  };

  // Task Item Component with Drag and Drop
  const TaskItem = ({ task, index }: { task: TodoItem, index: number }) => {
    const ref = React.useRef<HTMLLIElement>(null);
    
    const [{ isDragging }, drag] = useDrag({
      type: ITEM_TYPE,
      item: { index, id: task.id, type: ITEM_TYPE },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });
    
    const [{ isOver, canDrop }, drop] = useDrop({
      accept: ITEM_TYPE,
      hover: (item: DragItem, monitor) => {
        if (!ref.current) {
          return;
        }
        
        const dragIndex = item.index;
        const hoverIndex = index;
        
        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }

        // Determine rectangle on screen
        const hoverBoundingRect = ref.current.getBoundingClientRect();
        
        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        
        // Get mouse position
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        
        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;
        
        // Determine direction and perform move
        if (
          // Dragging downwards, cursor is past the middle
          (dragIndex < hoverIndex && hoverClientY > hoverMiddleY) ||
          // Dragging upwards, cursor is before the middle
          (dragIndex > hoverIndex && hoverClientY < hoverMiddleY)
        ) {
          // Time to actually perform the action
          moveTask(dragIndex, hoverIndex);
          
          // Note: we're mutating the monitor item here!
          // Generally it's better to avoid mutations,
          // but it's good here for the sake of performance
          // to avoid expensive index searches.
          item.index = hoverIndex;
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });
    
    // Initialize drag and drop refs
    drag(drop(ref));
    
    // Determine the appropriate background color based on state
    let bgColorClass = task.completed ? 'bg-gray-50 dark:bg-gray-800/50' : '';
    
    if (isDragging) {
      bgColorClass = 'opacity-50 bg-gray-100 dark:bg-gray-700';
    } else if (isOver && canDrop) {
      bgColorClass = 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800';
    }
    
    return (
      <li 
        ref={ref}
        className={`flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 group transition-colors ${bgColorClass} cursor-move`}
      >
        <div className="flex items-center flex-1">
          <div className="mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 touch-manipulation">
            <GripVertical className="h-5 w-5" />
          </div>
          <button 
            onClick={() => handleToggleComplete(task.id, task.completed)}
            className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 focus:outline-none mr-3"
          >
            {task.completed ? (
              <CheckSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
            ) : (
              <Square className="h-5 w-5" />
            )}
          </button>
          
          <span className={`flex-1 ${task.completed ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
            {task.title}
          </span>
          
          <button
            onClick={() => handleDeleteTask(task.id)}
            className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">To-Do Diario</h2>
        
        {completedTasksCount > 0 && (
          <Button
            variant="danger"
            size="sm"
            onClick={handleDeleteCompletedTasks}
          >
            Eliminar tareas completadas ({completedTasksCount})
          </Button>
        )}
      </div>

      <Card>
        <CardHeader title="Tareas (arrastra para reordenar)" />
        <CardContent>
          <div className="space-y-2">
            {/* Input for new task */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
              <input
                type="text"
                placeholder="Agregar nueva tarea..."
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                onKeyDown={handleAddTask}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white bg-white text-gray-900 placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>

            {/* Task list */}
            {localLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Cargando tareas...</p>
              </div>
            ) : tasks.length > 0 ? (
                <DndProvider backend={HTML5Backend}>
                  <ul className="space-y-2">
                    {tasks.map((task, index) => (
                      <TaskItem key={task.id} task={task} index={index} />
                    ))}
                  </ul>
                </DndProvider>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">No hay tareas pendientes</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    Escribe una tarea y presiona Enter para agregarla
                  </p>
                </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Single Task Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setTaskToDelete(null);
        }}
        onConfirm={confirmDeleteTask}
        title="Eliminar Tarea"
        message="¿Estás seguro de que deseas eliminar esta tarea? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />

      {/* Delete Completed Tasks Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteCompletedConfirmOpen}
        onClose={() => setDeleteCompletedConfirmOpen(false)}
        onConfirm={confirmDeleteCompletedTasks}
        title="Eliminar Tareas Completadas"
        message={`¿Estás seguro de que deseas eliminar todas las tareas completadas (${completedTasksCount})? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setError(null);
        }}
        message={error || 'Ha ocurrido un error inesperado'}
      />
    </div>
  );
};

export default TodoList;